import * as React from 'react'
import { useMemo } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import * as Dialog from '@radix-ui/react-dialog'
import clsx from 'clsx'
import type { PlateContentProps } from 'platejs/react'

import {
  Button,
  Flex,
  Heading,
  IconButton,
  Notice,
  SmileIcon,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type { TooltipProps } from '@buffer-mono/popcorn'
import { RichTextField } from '@buffer-mono/popcorn/rich-text'
import type { Uploader } from '@buffer-mono/uploader'
import { UploadSource } from '@buffer-mono/uploader'

import { EmojiPicker } from '~publish/components/EmojiPicker'
import { isIdeaComposerLocationState } from '~publish/components/IdeaManagementRouter/hooks'
import { capitalize } from '~publish/helpers/capitalize'
import { useCMDEnterKeys } from '~publish/hooks/useCMDEnterKeys'
import { AIAssistantTrigger } from '~publish/legacy/ai/components/Triggers'
import AIContextualMenu from '~publish/legacy/ai/components/Triggers/AIContextualMenu'
import { renderPlaceholderWithAIButton } from '~publish/legacy/ai/helpers/renderPlaceholderWithAIButton'
import {
  selectContentGenerationStatus,
  setAIAssistantPlacement,
  toggleAIAssistant,
} from '~publish/legacy/ai/state/slice'
import ModalActionCreators from '~publish/legacy/composer/composer/shared-components/modal/actionCreators'
import { getPlainText } from '~publish/legacy/editor/BufferEditor'
import type { BufferValue } from '~publish/legacy/editor/BufferEditor/types.plate'
import { EditorErrorBoundary } from '~publish/legacy/editor/components/EditorErrorBoundary'
import { useEmojiInsert } from '~publish/legacy/editor/plugins/emoji'
import {
  IntegrationsBar,
  useIntegrationServices,
} from '~publish/legacy/integrations-bar'
import integrationsBarStyles from '~publish/legacy/integrations-bar/styles.module.css'
import type { IntegrationsContext } from '~publish/legacy/integrations-bar/types'
import { selectShouldShowNBMigration } from '~publish/legacy/organizations/selectors'
import { TagsSelector } from '~publish/legacy/shared-components'
import { useAppDispatch, useAppSelector } from '~publish/legacy/store'
import {
  MAX_THUMBNAILS,
  MediaManager,
} from '~publish/legacy/uploads/components/MediaManager'
import {
  selectCompletedCount,
  selectIncludedMedia,
  selectPendingCount,
} from '~publish/legacy/uploads/state/selectors'
import { getRandomNumber } from '~publish/legacy/utils/numbers'
import { CreateUploadDropzone } from '~publish/pages/Create/components/CreateUploadDropzone' // Importing types from Create page is unavoidable for now
import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { useIsViewOnlyPublishUser } from '~publish/hooks/useIsViewOnlyPublishUser'

import { IDEAS_UPLOADER_ID } from '../constants'
import { isMediaConvertibleToPost } from '../helpers'
import { useIdeaComposerState } from '../IdeaComposer'
import { IDEA_EDITOR_ID } from './createIdeaEditor'
import { IdeaGroupSelector } from './IdeaGroupSelector'

import styles from './IdeaEditor.module.css'

const baseEditableProps: Omit<PlateContentProps, 'decorate'> = {
  placeholder: '',
}

const TooltipWrapper = ({ children, ...props }: TooltipProps): JSX.Element => {
  if (!props.content) {
    return <>{children}</>
  }
  return (
    <Tooltip {...props}>
      <div>{children}</div>
    </Tooltip>
  )
}
/**
 * Component responsibile for rendering the core content of the IdeaComposer.
 * It manages most of the state updates to the idea being created or edited.
 * It syncs all changes back to the parent IdeaComposer through context
 * and handles media uploads through a dedicated uploader instance.
 *
 * Accepts a container prop used to portal the Dialog.Content to the desired location.
 *
 * @example
 * ```tsx
 * <IdeaEditor container={document.getElementById('portal-root')} />
 * ```
 */
export const IdeaEditor = ({
  container,
  uploader,
  className,
  presentation = 'dialog',
  headerAction,
}: {
  container?: HTMLElement | null
  uploader: Uploader
  className?: string
  /**
   * The editor renders inside a Radix Dialog by default; the inline side
   * panel has no Dialog context, so Radix accessibility primitives must be
   * skipped there.
   */
  presentation?: 'dialog' | 'panel'
  /**
   * Rendered at the end of the header's properties row. The panel puts its
   * close button here; the dialog closes from outside the card instead.
   */
  headerAction?: React.ReactNode
}): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const locationState = isIdeaComposerLocationState(location.state)
    ? location.state
    : undefined
  const scrollableContentRef = React.useRef<HTMLDivElement>(null)
  const integrationsSentinelRef = React.useRef<HTMLDivElement>(null)
  const [isIntegrationsBarStuck, setIsIntegrationsBarStuck] =
    React.useState(false)

  // Sentinel sits below the sticky integrations bar; when it scrolls
  // out of view, the bar is actively pinned and we render its top border.
  React.useEffect(() => {
    const sentinel = integrationsSentinelRef.current
    const scrollRoot = scrollableContentRef.current
    if (!sentinel || !scrollRoot) return
    const observer = new IntersectionObserver(
      ([entry]) => setIsIntegrationsBarStuck(!entry.isIntersecting),
      { root: scrollRoot, threshold: 0 },
    )
    observer.observe(sentinel)
    return (): void => observer.disconnect()
  }, [])

  const isViewOnlyPublishUser = useIsViewOnlyPublishUser()
  const canManageIdeas = useHasOrganizationAction('manageIdeas')

  const {
    editor,
    draftIdea,
    saveDraftIdea,
    setGroupId,
    setTitle,
    setText,
    setSelectedTags,
    error,
    isLoading,
    createPostFromIdea,
  } = useIdeaComposerState()

  // This function is passed to Plate to be run on each editor update
  // to update the draftIdea text state
  const updateTextOnEditorValueChange = React.useCallback(
    ({ value: editorValue }: { value: BufferValue }) => {
      const updatedText = getPlainText(editor)
      if (
        editorValue &&
        draftIdea?.content?.text?.trim() !== updatedText?.trim()
      ) {
        setText(updatedText)
      }
    },
    [draftIdea?.content?.text, editor, setText],
  )

  const [ctaPostTooltipLabel, setCtaPostTooltipLabel] = React.useState<
    string | null
  >(null)

  const { contentGenerationInProgress, contentGenerationError } =
    useAppSelector((state) => selectContentGenerationStatus(state))

  const context: IntegrationsContext = 'ideasEditor'

  /** Uploads and Integrations */
  const { services, activeService } = useIntegrationServices({
    uploader,
    context,
  })

  const pendingCount = useAppSelector((state) =>
    selectPendingCount(state, IDEAS_UPLOADER_ID),
  )
  const completedCount = useAppSelector((state) =>
    selectCompletedCount(state, IDEAS_UPLOADER_ID),
  )
  const isUploading = pendingCount > 0

  const media = useAppSelector((state) =>
    selectIncludedMedia(state, IDEAS_UPLOADER_ID),
  )

  const onUploadDrop = React.useCallback(
    (files: File[]) => {
      void uploader.addFiles(files, { source: UploadSource.dragAndDrop() })
    },
    [uploader],
  )
  const onUploadPaste = React.useCallback(
    (files: File[]) => {
      void uploader.addFiles(files, { source: UploadSource.paste() })
    },
    [uploader],
  )
  const shouldShowNBMigration = useAppSelector(selectShouldShowNBMigration)
  const onOpenAIAssistant = (): void => {
    // Sets the placement (source) for tracking
    // Must be set before the upgrade modal is triggered as the modal
    // relies on the placement for tracking.
    dispatch(setAIAssistantPlacement({ placement: 'ideasEditor' }))

    // Display a upgrade flow for multi-product users
    // Restrict AI Assistant feature to New Buffer users only
    if (shouldShowNBMigration) {
      ModalActionCreators.openModal('AIAssistantMPUpgradePlan', {
        ctaButton: 'integrationsBar',
      })
      return
    }
    dispatch(toggleAIAssistant(true))
  }

  /** Plate Editor */
  const placeholder = t(`content.editor.placeholder.${getRandomNumber(10)}`)

  baseEditableProps.placeholder = placeholder.concat(' or ')
  baseEditableProps.renderPlaceholder = renderPlaceholderWithAIButton({
    onToggle: onOpenAIAssistant,
  })

  const editableProps: Omit<PlateContentProps, 'decorate' | 'readOnly'> =
    useMemo(
      () => ({
        ...baseEditableProps,
        // The placeholder carries the AI-assistant button, which is a write
        // affordance — an empty read-only idea shows no placeholder at all.
        ...(canManageIdeas
          ? {}
          : { placeholder: '', renderPlaceholder: undefined }),
        spellCheck: true,
        // readOnly is passed to RichTextField.Root directly, not the editable.
        // See ComposerEditor: guard the contenteditable so browser translation
        // can't rewrite (and corrupt/crash) the idea body inside Slate's DOM.
        translate: 'no',
        'data-draftid': IDEA_EDITOR_ID,
        'data-testid': IDEA_EDITOR_ID,
        className: styles.editable,
        style: { height: '100%' },
      }),
      [canManageIdeas],
    )

  const createPostButtonDisabled = useMemo(() => {
    const hasMedia = completedCount !== 0
    const hasTextOrMedia = draftIdea?.content?.text || hasMedia

    const { valid: isMediaConvertible, message } =
      isMediaConvertibleToPost(media)

    setCtaPostTooltipLabel(message)
    return (
      isUploading ||
      !hasTextOrMedia ||
      !isMediaConvertible ||
      contentGenerationInProgress ||
      isLoading
    )
  }, [
    draftIdea?.content?.text,
    media,
    contentGenerationInProgress,
    completedCount,
    isUploading,
    isLoading,
  ])

  const saveButtonDisabled = useMemo(() => {
    const hasMedia = completedCount !== 0
    return (
      isUploading ||
      (!draftIdea?.content?.text && !hasMedia && !draftIdea?.content?.title) ||
      contentGenerationInProgress ||
      isLoading
    )
  }, [
    draftIdea?.content?.text,
    isUploading,
    draftIdea?.content?.title,
    completedCount,
    contentGenerationInProgress,
    isLoading,
  ])

  const postTooltipLabel = createPostButtonDisabled
    ? 'Include copy or media to create a post from this Idea'
    : ''

  const saveTooltipLabel = saveButtonDisabled
    ? 'Include a title, copy, or media to save this Idea'
    : ''

  useCMDEnterKeys(saveDraftIdea, !saveButtonDisabled && canManageIdeas)

  const errorMessage = contentGenerationError || error?.message
  const composerTitle = !canManageIdeas
    ? 'View'
    : locationState?.action
      ? capitalize(locationState?.action)
      : draftIdea?.id
        ? 'Edit'
        : 'New'

  const handleEmojiSelect = useEmojiInsert({ editor })

  return (
    <>
      {errorMessage && (
        <Notice variant="error" className={styles.errorNotice}>
          <Text>{errorMessage}</Text>
        </Notice>
      )}
      <CreateUploadDropzone
        disabled={!canManageIdeas || completedCount + pendingCount >= 10}
        onDrop={onUploadDrop}
        onPaste={onUploadPaste}
      >
        <Flex
          className={clsx(styles.ideaEditor, className)}
          direction="column"
          justify="between"
          align="stretch"
        >
          <div className={styles.ideatorHeader}>
            <div className={styles.headerRow}>
              {presentation === 'dialog' ? (
                <Dialog.Description asChild>
                  <Heading as="h2">{composerTitle} Idea</Heading>
                </Dialog.Description>
              ) : (
                <Heading as="h2">{composerTitle} Idea</Heading>
              )}
              <Flex
                gap="xs"
                align="center"
                className={clsx(
                  styles.ideaProperties,
                  presentation === 'panel' && styles.ideaPropertiesInline,
                )}
              >
                <IdeaGroupSelector
                  selectedGroupId={draftIdea?.groupId}
                  onGroupSelect={setGroupId}
                  disabled={!canManageIdeas}
                />
                <TagsSelector
                  preSelectedTags={draftIdea?.content?.tags ?? []}
                  onSelectTags={setSelectedTags}
                  disabled={!canManageIdeas}
                />
                {headerAction}
              </Flex>
            </div>
            <VisuallyHidden as="label" htmlFor="idea-title">
              Idea title
            </VisuallyHidden>
            {/* oxlint-disable-next-line jsx-a11y/control-has-associated-label -- VisuallyHidden renders the native label associated with this input by htmlFor. */}
            <input
              className={styles.ideaTitleInput}
              id="idea-title"
              placeholder={canManageIdeas ? 'Give your idea a title' : ''}
              translate="no"
              type="text"
              readOnly={!canManageIdeas}
              value={draftIdea?.content?.title ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                setTitle(e.target.value)
              }
            />
          </div>
          <div className={styles.scrollableContent} ref={scrollableContentRef}>
            <div className={styles.ideatorContent}>
              <EditorErrorBoundary editor={editor}>
                <RichTextField.Root
                  editor={editor}
                  onChange={updateTextOnEditorValueChange}
                  readOnly={!canManageIdeas}
                  variant="embedded"
                  className={styles.richTextFieldRoot}
                >
                  <RichTextField.Editable {...editableProps} />
                  <AIContextualMenu editor={editor} placement="ideasEditor" />
                </RichTextField.Root>
              </EditorErrorBoundary>
            </div>
            <div
              className={styles.mediaManagerWrapper}
              id="ideas-media-manager"
            >
              <MediaManager
                id="ideas"
                containerRef={container}
                uploader={uploader}
                readOnly={!canManageIdeas}
              />
            </div>
            {canManageIdeas && (
              <div
                className={styles.integrationsWrapper}
                data-stuck={isIntegrationsBarStuck}
              >
                <IntegrationsBar
                  context={context}
                  services={services}
                  activeService={activeService}
                  disableBar={completedCount + pendingCount >= MAX_THUMBNAILS}
                  container={container}
                >
                  <div className={integrationsBarStyles.verticalDivider} />
                  <EmojiPicker onEmojiSelect={handleEmojiSelect}>
                    <IconButton
                      data-testid="emoji-picker-button"
                      aria-label="Select emoji"
                      label="Select emoji"
                      variant="tertiary"
                      tooltip="Select emoji"
                    >
                      <SmileIcon />
                    </IconButton>
                  </EmojiPicker>
                </IntegrationsBar>
                <div className={integrationsBarStyles.verticalDivider} />
                <AIAssistantTrigger onClick={onOpenAIAssistant} />
              </div>
            )}
            <div ref={integrationsSentinelRef} aria-hidden="true" />
          </div>
          {canManageIdeas && (
            <div className={styles.buttonWrapper} id="ideas-actions">
              {!isViewOnlyPublishUser && (
                <TooltipWrapper
                  content={ctaPostTooltipLabel ?? postTooltipLabel}
                >
                  <Button
                    variant="secondary"
                    size="large"
                    onClick={createPostFromIdea}
                    disabled={createPostButtonDisabled}
                  >
                    Create Post
                  </Button>
                </TooltipWrapper>
              )}
              <TooltipWrapper content={saveTooltipLabel}>
                <Button
                  variant="primary"
                  size="large"
                  onClick={saveDraftIdea}
                  disabled={saveButtonDisabled}
                >
                  Save Idea
                </Button>
              </TooltipWrapper>
            </div>
          )}
        </Flex>
      </CreateUploadDropzone>
    </>
  )
}
